import { CashBackType, FundingMethod, FundingType, type GetFundingRequestV2 } from "kz-ui-sdk";

type TxHistoryQuery = GetFundingRequestV2["query"];

/** Query params a row sends. Built from the funding-history query so the two stay in sync. */
export type TxTypeQuery = Required<Pick<TxHistoryQuery, "type">> & Pick<TxHistoryQuery, "method">;

/** A promotion `method` value: either a FundingMethod or a short CashBackType. */
export type PromotionMethod = FundingMethod | CashBackType;

interface TxTypeFilterOptionBase {
  id: string;
  labelKey: string;
}

/** A row that filters. Tapping it applies `query`. */
export interface TxTypeFilterLeaf extends TxTypeFilterOptionBase {
  query: TxTypeQuery;
  children?: never;
}

/** A row that only expands. Tapping it toggles `children`. Only two levels are supported. */
export interface TxTypeFilterGroup extends TxTypeFilterOptionBase {
  query?: never;
  children: TxTypeFilterLeaf[];
}

export type TxTypeFilterOption = TxTypeFilterLeaf | TxTypeFilterGroup;

const FILTERABLE_TX_TYPES = [FundingType.Deposit, FundingType.Withdraw, FundingType.Adjustment, FundingType.Promotion];

/** Builds a promotion sub-type row. Pass several methods if they share one label. */
const createPromotionOption = (id: string, labelKey: string, ...methods: PromotionMethod[]): TxTypeFilterLeaf => ({
  id,
  labelKey,
  query: { type: FundingType.Promotion, method: methods.length ? methods.join(",") : undefined },
});

const PROMOTION_SUB_TYPES: TxTypeFilterLeaf[] = [
  createPromotionOption("promotion-all", "all promotions"),
  // `daily-bonus` and `daily-streak` both show as "Daily Free Bonus" (see getTxRemark),
  createPromotionOption("daily-bonus", "daily bonus", FundingMethod.DailyBonus, FundingMethod.DailyStreak),
  createPromotionOption("referral-token", "refer balls bonus", FundingMethod.ReferralToken),
  createPromotionOption("cashback-instant", "instant cashback", CashBackType.Instant),
  createPromotionOption("cashback-daily", "daily cashback", CashBackType.Daily),
  createPromotionOption("cashback-weekly", "weekly cashback", CashBackType.Weekly),
  createPromotionOption("cashback-monthly", "monthly cashback", CashBackType.Monthly),
  createPromotionOption("referral-commission", "refer & earn commissions", FundingMethod.ReferralCommission),
  createPromotionOption("play-spin", "play and spin", FundingMethod.PlaySpin),
  createPromotionOption("skinner-box", "surprise box", FundingMethod.SkinnerBox),
  createPromotionOption("compensation", "customer service gift", FundingMethod.Compensation),
  createPromotionOption("mega-share", "mega share day", FundingMethod.MegaShare),
  createPromotionOption("secret-code", "free code", FundingMethod.SecretCode),
  createPromotionOption("cashback-mission", "cashback mission", FundingMethod.CashbackMission),
  createPromotionOption("vip-cashback-bonus", "vip cashback bonus", FundingMethod.VipCashbackBonus),
];

export const TX_TYPE_FILTER_ALL = {
  id: "all",
  labelKey: "All Transactions",
  // Transfer is included silently. It is the internal side of Adjustment.
  query: { type: [...FILTERABLE_TX_TYPES, FundingType.Transfer].join(",") },
} satisfies TxTypeFilterLeaf;

export const TX_TYPE_FILTER_OPTIONS: TxTypeFilterOption[] = [
  TX_TYPE_FILTER_ALL,
  { id: FundingType.Deposit, labelKey: "deposit", query: { type: FundingType.Deposit } },
  { id: FundingType.Withdraw, labelKey: "withdraw", query: { type: FundingType.Withdraw } },
  {
    id: FundingType.Adjustment,
    labelKey: "adjustment",
    query: { type: `${FundingType.Adjustment},${FundingType.Transfer}` },
  },
  // No `query`, so tapping this row expands it instead of filtering.
  // Label is singular ("Promotion") to match the title on each transaction row.
  // Only the "All Promotions" child is plural.
  { id: FundingType.Promotion, labelKey: "promotion", children: PROMOTION_SUB_TYPES },
];
